import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { outboxApi } from '../../api/outbox'
import { RefusedError } from '../../api/client'
import { Correct } from '../../components/Correct'
import { useSpoke } from '../../hooks/useSpoke'
import { groupByStatus, sentSummary } from '../../lib/outbox'
import type { Outcome, OutboundView } from '../../types/outbox'
import { GrantPanel, OutboxRow, OutboxSection } from './OutboxParts'
import './outbox.css'

// THE OUTBOX — every message drafted from the list: what is still a draft, what is approved and waiting
// for your click, what went out and how it landed. Personal: the API answers for the signed-in person
// and ignores ?org=. The measure runs nightly; your own word on an outcome is final.

export function Outbox() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const location = useLocation()
  const q = useQuery({ queryKey: ['outbox', spoke], queryFn: () => outboxApi.list(spoke), staleTime: 10_000 })
  const refresh = () => void qc.invalidateQueries({ queryKey: ['outbox'] })
  const outcome = useMutation({ mutationFn: ({ id, o }: { id: string; o: Outcome }) => outboxApi.outcome(spoke, id, o), onSuccess: refresh })
  const start = useMutation({ mutationFn: () => outboxApi.grantStart(spoke), onSuccess: (r) => { window.location.assign(r.authorizeUrl) } })
  const revoke = useMutation({ mutationFn: () => outboxApi.revokeGrant(spoke), onSuccess: refresh })

  const params = new URLSearchParams(location.search)
  const notice = params.get('granted') ? 'Done: you can send from your mailbox now, one message at a time.' : params.get('error')
  const failed = [outcome, start, revoke].find((m) => m.isError)?.error
  const busy = outcome.isPending || start.isPending || revoke.isPending

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError || !q.data) {
    return (
      <section className="page outbox">
        <p className="kicker">Outbox</p>
        <h1>What you have written</h1>
        <div className="refusal"><strong>Nothing to read yet</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error?.message ?? 'Nothing here.'}</p></div>
      </section>
    )
  }

  const groups = groupByStatus(q.data.messages)
  const row = (v: OutboundView, withOutcome: boolean) => (
    <OutboxRow key={v.row.outboundId} view={v} busy={busy}
      onOutcome={withOutcome ? (o) => outcome.mutate({ id: v.row.outboundId, o }) : undefined}
      correct={v.row.applicationId ? <Correct kind="play" entityId={v.row.applicationId} entityLabel={v.tacticName ?? 'this play'} currentValue={v.row.tacticId ? `tactic:${v.row.tacticId}` : null} compact /> : undefined} />
  )

  return (
    <section className="page outbox">
      <header className="deals-head">
        <div>
          <p className="kicker">Your own · Outbox</p>
          <h1>What you have written</h1>
          <p className="lede">{sentSummary(groups.sent)} Every message here was written to a play from the <Link to="/playbook">playbook</Link>; what came back is what the playbook measures.</p>
        </div>
        <div className="actions"><Link className="button" to="/outreach">Who to contact today</Link></div>
      </header>

      {failed && <p className="error small" role="alert">{failed instanceof RefusedError ? failed.refusal.message : (failed as Error).message}</p>}

      <GrantPanel grant={q.data.grant} onStart={() => start.mutate()} onRevoke={() => revoke.mutate()} busy={busy} notice={notice} />

      <OutboxSection title="Drafts" messages={groups.drafted} empty="No drafts. Write one from a person on your Outreach list." render={(v) => row(v, false)} />
      <OutboxSection title="Approved, not sent" messages={groups.approved} empty="Nothing approved and waiting." render={(v) => row(v, false)} />
      <OutboxSection title="Sent" messages={groups.sent} empty="Nothing sent yet." render={(v) => row(v, true)} />
    </section>
  )
}
